import { useState } from "react";
import { Mail, Instagram, Heart, CheckCircle2, DollarSign } from "lucide-react";
import { submitContact } from "../lib/api";

const FIELD = "w-full rounded-2xl border border-[var(--sss-border)] bg-white px-4 py-3 text-sm focus:outline-none focus:border-[var(--sss-pink)] focus:ring-2 focus:ring-[var(--sss-pink)]/20 transition";

export default function Contact() {
    const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState(null);

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError(null);
        try {
            await submitContact(form);
            setSent(true);
            setForm({ name: "", email: "", subject: "", message: "" });
        } catch (err) {
            setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-14" data-testid="contact-page">
            <div className="text-center">
                <p className="text-xs uppercase tracking-[0.3em] text-[var(--sss-pink)] font-semibold">Say Hello</p>
                <h1 className="font-serif-display text-5xl md:text-6xl font-bold mt-3">Contact</h1>
                <p className="mt-3 text-[var(--sss-text-muted)]">We&apos;d love to hear from you.</p>
            </div>

            <div className="mt-10 grid md:grid-cols-5 gap-8">
                <div className="md:col-span-2 space-y-4">
                    <a href="mailto:Laylah@sugarsweetsociety.com" className="flex items-start gap-3 p-5 bg-white rounded-2xl sss-shadow hover:-translate-y-0.5 transition" data-testid="contact-email-link">
                        <Mail size={20} className="text-[var(--sss-pink)] mt-0.5" />
                        <div>
                            <p className="text-xs uppercase tracking-wider font-semibold text-[var(--sss-text-muted)]">Email</p>
                            <p className="font-medium">Laylah@sugarsweetsociety.com</p>
                        </div>
                    </a>
                    <a href="https://instagram.com/sugarsweet.society" target="_blank" rel="noreferrer" className="flex items-start gap-3 p-5 bg-white rounded-2xl sss-shadow hover:-translate-y-0.5 transition" data-testid="contact-instagram-link">
                        <Instagram size={20} className="text-[var(--sss-pink)] mt-0.5" />
                        <div>
                            <p className="text-xs uppercase tracking-wider font-semibold text-[var(--sss-text-muted)]">Instagram</p>
                            <p className="font-medium">@sugarsweet.society</p>
                        </div>
                    </a>
                    <a href="https://cash.app/$Sugarsweetsociety1L" target="_blank" rel="noreferrer" className="flex items-start gap-3 p-5 bg-white rounded-2xl sss-shadow hover:-translate-y-0.5 transition" data-testid="contact-cashapp-link">
                        <DollarSign size={20} className="text-[var(--sss-pink)] mt-0.5" />
                        <div>
                            <p className="text-xs uppercase tracking-wider font-semibold text-[var(--sss-text-muted)]">Cash App</p>
                            <p className="font-medium">$Sugarsweetsociety1L</p>
                        </div>
                    </a>
                    <div className="p-5 bg-[var(--sss-blush)] rounded-2xl">
                        <p className="text-xs uppercase tracking-wider font-semibold text-[var(--sss-pink)]">Payment Options</p>
                        <p className="mt-1 text-sm">PayPal · Cash App · Cash · Apple Pay</p>
                    </div>
                </div>

                <div className="md:col-span-3">
                    {sent ? (
                        <div className="bg-white rounded-3xl sss-shadow p-8 text-center" data-testid="contact-success">
                            <div className="mx-auto w-14 h-14 rounded-full bg-[var(--sss-blush)] flex items-center justify-center">
                                <CheckCircle2 size={28} className="text-[var(--sss-pink)]" strokeWidth={1.5} />
                            </div>
                            <h2 className="font-serif-display text-2xl mt-4 font-semibold">Message sent!</h2>
                            <p className="mt-2 text-sm text-[var(--sss-text-muted)]">We&apos;ll reply as soon as we can.</p>
                            <button type="button" onClick={() => setSent(false)} className="btn-pink mt-6" data-testid="contact-send-another">Send Another</button>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="bg-white rounded-3xl sss-shadow p-6 md:p-8 space-y-4" data-testid="contact-form">
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="text-xs uppercase tracking-wider font-semibold text-[var(--sss-text-muted)]" htmlFor="c-name">Name *</label>
                                    <input id="c-name" required value={form.name} onChange={update("name")} className={FIELD + " mt-1"} data-testid="contact-name" />
                                </div>
                                <div>
                                    <label className="text-xs uppercase tracking-wider font-semibold text-[var(--sss-text-muted)]" htmlFor="c-email">Email *</label>
                                    <input id="c-email" type="email" required value={form.email} onChange={update("email")} className={FIELD + " mt-1"} data-testid="contact-email" />
                                </div>
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-wider font-semibold text-[var(--sss-text-muted)]" htmlFor="c-subject">Subject</label>
                                <input id="c-subject" value={form.subject} onChange={update("subject")} className={FIELD + " mt-1"} data-testid="contact-subject" />
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-wider font-semibold text-[var(--sss-text-muted)]" htmlFor="c-message">Message *</label>
                                <textarea id="c-message" rows={5} required value={form.message} onChange={update("message")} className={FIELD + " mt-1 resize-none"} data-testid="contact-message" />
                            </div>
                            {error && <p className="text-sm text-red-600" data-testid="contact-error">{error}</p>}
                            <button type="submit" disabled={submitting} className="btn-pink w-full justify-center" data-testid="contact-submit">
                                {submitting ? "Sending…" : <>Send Message <Heart size={14} fill="#fff" strokeWidth={0} /></>}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}
